import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { BandPill } from "@/components/Bands";
import { SUBJECT_META } from "@/lib/scale";
import { ArrowLeft, Printer, Loader2, Map as MapIcon, ArrowDownUp } from "lucide-react";

const PROGRESS = {
  not_achieved: { label: "Just starting", color: "#C97064" },
  acquiring: { label: "Getting there", color: "#b07d3a" },
  becoming_fluent: { label: "Almost there", color: "#6C8EBF" },
  achieved: { label: "Achieved", color: "#5d8a73" },
};
const STAGE_SHORT = (s) => (s ? s.replace("Classroom Support", "Class").replace("School Support Plus", "School+").replace("School Support", "School") : "—");

const REVIEW_META = {
  overdue: { label: "Overdue", color: "#C97064", bg: "#F7E4E0" },
  due_soon: { label: "Due soon", color: "#9a6a2a", bg: "#F6ECDA" },
  scheduled: { label: "Scheduled", color: "#5d8a73", bg: "#E6F0EA" },
  none: { label: "—", color: "#94a3b8", bg: "transparent" },
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IE", { day: "numeric", month: "short" }); } catch { return ""; }
};

export default function OverviewMap() {
  const { classId, groupId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reviewFilter, setReviewFilter] = useState("all"); // all | due | overdue
  const [sortBy, setSortBy] = useState("name"); // name | review | low_literacy | low_numeracy | low_social_emotional

  useEffect(() => {
    const url = classId ? `/classes/${classId}/summary` : `/groups/${groupId}/summary`;
    api.get(url).then((r) => setData(r.data)).catch(() => navigate("/"));
  }, [classId, groupId, navigate]);

  const allStudents = useMemo(() => data?.students || [], [data]);

  const overdueCount = useMemo(
    () => allStudents.filter((s) => s.review?.flag === "overdue").length, [allStudents]);

  const students = useMemo(() => {
    let list = [...allStudents];
    if (reviewFilter === "overdue") list = list.filter((s) => s.review?.flag === "overdue");
    else if (reviewFilter === "due") list = list.filter((s) => ["overdue", "due_soon"].includes(s.review?.flag));

    const bandOf = (s, subj) => (s.bands?.[subj]?.band ?? 99);
    if (sortBy === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "review") {
      const rank = { overdue: 0, due_soon: 1, scheduled: 2, none: 3 };
      list.sort((a, b) => (rank[a.review?.flag] ?? 3) - (rank[b.review?.flag] ?? 3));
    } else if (sortBy.startsWith("low_")) {
      const subj = sortBy.slice(4);
      list.sort((a, b) => bandOf(a, subj) - bandOf(b, subj));
    }
    return list;
  }, [allStudents, reviewFilter, sortBy]);

  if (!data) return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const title = data.class_info ? data.class_info.name : data.group_info?.name;
  const subtitle = data.class_info ? data.class_info.level : "Support group";
  const isGroup = !!data.group_info;

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      <div className="max-w-6xl mx-auto flex items-center justify-between mb-6 no-print">
        <button data-testid="map-back-btn" onClick={() => navigate(isGroup ? `/group/${groupId}` : `/class/${classId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={16} /> Back
        </button>
        <button data-testid="map-print-btn" onClick={() => window.print()}
          className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2">
          <Printer size={17} /> Print / Save PDF
        </button>
      </div>

      <div className="print-area max-w-6xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] p-6 sm:p-8">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-5">
          <span className="h-11 w-11 rounded-2xl bg-[#44546A] text-white flex items-center justify-center"><MapIcon size={20} /></span>
          <div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-slate-800">Complete Learning Map</h1>
            <p className="text-sm text-slate-500">{title} · {subtitle} · {students.length}{students.length !== allStudents.length ? ` of ${allStudents.length}` : ""} {allStudents.length === 1 ? "pupil" : "pupils"}</p>
          </div>
        </div>

        <div className="no-print flex flex-wrap items-center gap-2 mb-5" data-testid="map-toolbar">
          <div className="inline-flex rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
            {[
              { k: "all", label: "All pupils" },
              { k: "due", label: "Reviews due/overdue" },
              { k: "overdue", label: `Overdue${overdueCount ? ` (${overdueCount})` : ""}` },
            ].map((o) => (
              <button key={o.k} data-testid={`map-filter-${o.k}`} onClick={() => setReviewFilter(o.k)}
                className={`px-3.5 py-1.5 rounded-full transition ${reviewFilter === o.k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {o.label}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1.5 ml-auto">
            <ArrowDownUp size={14} className="text-slate-400" />
            <select data-testid="map-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="text-xs font-semibold text-slate-700 bg-slate-100 rounded-full px-3 py-1.5 outline-none cursor-pointer">
              <option value="name">Sort: Name (A–Z)</option>
              <option value="review">Sort: Review (overdue first)</option>
              {Object.keys(SUBJECT_META).map((k) => (
                <option key={k} value={`low_${k}`}>{`Sort: Lowest ${SUBJECT_META[k].name.split(" ")[0]} band`}</option>
              ))}
            </select>
          </div>
        </div>

        {students.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8" data-testid="map-empty">{allStudents.length === 0 ? "No pupils to show yet." : "No pupils match this filter."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="map-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-bold border-b-2 border-slate-100">
                  <th className="py-2 pr-3">Pupil</th>
                  {isGroup && <th className="py-2 pr-3">Class</th>}
                  {Object.keys(SUBJECT_META).map((k) => (
                    <th key={k} className="py-2 pr-3 text-center">{SUBJECT_META[k].name.split(" ")[0]}</th>
                  ))}
                  <th className="py-2 pr-3 text-center">Support</th>
                  <th className="py-2 pr-3 text-center">Review</th>
                  <th className="py-2 pr-3">Priority targets</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const counts = {};
                  (s.targets || []).forEach((t) => { counts[t.progress] = (counts[t.progress] || 0) + 1; });
                  const total = (s.targets || []).length;
                  return (
                    <tr key={s.id} data-testid={`map-row-${s.id}`} className="border-b border-slate-50 align-middle">
                      <td className="py-2.5 pr-3 font-semibold text-slate-800">
                        <button onClick={() => navigate(`/student/${s.id}`)} className="hover:text-[#5d8a73] transition no-print text-left">{s.name}</button>
                        <span className="hidden print:inline">{s.name}</span>
                      </td>
                      {isGroup && <td className="py-2.5 pr-3 text-slate-500 text-xs">{s.class_name}</td>}
                      {Object.keys(SUBJECT_META).map((k) => (
                        <td key={k} className="py-2.5 pr-3 text-center">
                          {s.bands?.[k] ? <BandPill bandKey={s.bands[k].band} label={`${s.bands[k].total}/45`} testId={`map-band-${s.id}-${k}`} /> : <span className="text-slate-300">—</span>}
                        </td>
                      ))}
                      <td className="py-2.5 pr-3 text-center text-xs text-slate-600">{STAGE_SHORT(s.stage)}</td>
                      <td className="py-2.5 pr-3 text-center">
                        {(() => {
                          const flag = s.review?.flag || "none";
                          const m = REVIEW_META[flag];
                          if (flag === "none") return <span className="text-slate-300 text-xs">—</span>;
                          return (
                            <span data-testid={`map-review-${s.id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                              style={{ color: m.color, backgroundColor: m.bg }}>
                              {m.label}{s.review?.review_date ? ` · ${fmtDate(s.review.review_date)}` : ""}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2.5 pr-3">
                        {total === 0 ? <span className="text-slate-300 text-xs">None</span> : (
                          <div className="flex items-center gap-2">
                            <div className="flex h-2 w-24 rounded-full overflow-hidden bg-slate-100">
                              {Object.keys(PROGRESS).map((p) => counts[p] ? (
                                <div key={p} title={`${counts[p]} ${PROGRESS[p].label}`} style={{ width: `${(counts[p] / total) * 100}%`, backgroundColor: PROGRESS[p].color }} />
                              ) : null)}
                            </div>
                            <span className="text-xs text-slate-500">{total}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-5 text-[11px] text-slate-500">
          {Object.entries(PROGRESS).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: v.color }} /> {v.label}</span>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-6 pt-4 border-t border-slate-100 text-center">
          Generated with BloomTrack · Bands show latest assessment total /45 per area · Scale 1–9 per skill
        </p>
      </div>
    </div>
  );
}
